import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, FileText, Zap, ShieldCheck } from "lucide-react";
import { toast, Toaster } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EstimatePublic() {
  const { token } = useParams();
  const [est, setEst] = useState(null);
  const [loading, setLoading] = useState(true);
  const [decideOpen, setDecideOpen] = useState(null); // "accept" | "decline"
  const [signature, setSignature] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/public/estimates/${token}`);
      setEst(data);
    } catch (e) {
      setEst({ error: e.response?.status === 404 ? "not_found" : "error" });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [token]); // eslint-disable-line

  const decide = async (kind) => {
    setSubmitting(true);
    try {
      if (kind === "accept") {
        await axios.post(`${API}/public/estimates/${token}/accept`, { signature_name: signature });
      } else {
        await axios.post(`${API}/public/estimates/${token}/decline`, { decline_reason: reason });
      }
      toast.success(kind === "accept" ? "Thanks — we'll be in touch!" : "Thanks for letting us know.");
      setDecideOpen(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSubmitting(false); }
  };

  if (loading) return <FullPage><div className="text-zinc-500">Loading…</div></FullPage>;
  if (!est || est.error) return <FullPage><div className="text-center"><FileText className="w-10 h-10 text-zinc-300 mx-auto mb-3"/><div className="font-heading text-2xl font-bold">Estimate not found</div><div className="text-sm text-zinc-500 mt-2">This link may have expired or been removed.</div></div></FullPage>;

  const decided = est.status === "accepted" || est.status === "declined";

  return (
    <div className="min-h-screen bg-zinc-50 py-10 px-4">
      <Toaster richColors position="top-right"/>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-zinc-900 text-white px-3 py-1.5 rounded-lg">
            <Zap className="w-4 h-4 text-[#FF5A00]"/>
            <span className="font-heading font-bold">{est.business?.name || "LeadForge"}</span>
          </div>
          {(est.business?.address || est.business?.phone || est.business?.email) && (
            <div className="mt-2 text-xs text-zinc-600" data-testid="estimate-public-biz-info">
              {[est.business.address, est.business.phone, est.business.email].filter(Boolean).join(" · ")}
            </div>
          )}
          <div className="mt-3 text-xs font-mono-accent uppercase tracking-widest text-zinc-500">Estimate {est.estimate_number}</div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden shadow-sm">
          {/* Styled document — same HTML used for PDF/email/print */}
          <iframe
            title="estimate"
            src={`${API}/public/estimates/${token}/html`}
            className="w-full bg-white"
            style={{ minHeight: "1100px", border: "none", display: "block" }}
            data-testid="estimate-doc-iframe"
          />

          {/* Status pill */}
          {est.status === "accepted" && (
            <div className="mx-8 mt-2 mb-6 flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <CheckCircle2 className="w-6 h-6 text-emerald-600"/>
              <div>
                <div className="font-medium text-emerald-900">Estimate accepted</div>
                <div className="text-xs text-emerald-700">We received your acceptance on {new Date(est.accepted_at).toLocaleDateString()}. We'll be in touch shortly.</div>
              </div>
            </div>
          )}
          {est.status === "declined" && (
            <div className="mx-8 mt-2 mb-6 flex items-center gap-3 bg-zinc-100 border border-zinc-200 rounded-lg p-4">
              <XCircle className="w-6 h-6 text-zinc-500"/>
              <div>
                <div className="font-medium text-zinc-800">Estimate declined</div>
                <div className="text-xs text-zinc-500">Thanks for the update. Reach out anytime if things change.</div>
              </div>
            </div>
          )}

          {!decided && (
            <div className="bg-zinc-50 border-t border-zinc-200 p-6 flex flex-col sm:flex-row gap-3">
              <button
                onClick={()=>setDecideOpen("accept")}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 btn-lift"
                data-testid="public-accept-btn"
              >
                <CheckCircle2 className="w-5 h-5"/> Accept estimate
              </button>
              <button
                onClick={()=>setDecideOpen("decline")}
                className="flex-1 bg-white hover:bg-zinc-100 border border-zinc-300 text-zinc-800 font-semibold py-3 rounded-lg flex items-center justify-center gap-2"
                data-testid="public-decline-btn"
              >
                <XCircle className="w-5 h-5"/> Decline
              </button>
            </div>
          )}
        </div>

        <div className="text-center mt-6 text-xs text-zinc-500 flex items-center justify-center gap-1">
          <ShieldCheck className="w-3 h-3"/> Secure estimate powered by LeadForge
        </div>
      </div>

      {/* Decide modal */}
      {decideOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={()=>setDecideOpen(null)}>
          <div className="bg-white rounded-xl p-6 max-w-md w-full" onClick={(e)=>e.stopPropagation()} data-testid="public-decide-dialog">
            <h3 className="font-heading text-xl font-bold mb-2">{decideOpen === "accept" ? "Accept this estimate?" : "Decline this estimate?"}</h3>
            {decideOpen === "accept" ? (
              <>
                <p className="text-sm text-zinc-600 mb-4">Type your full name below to sign electronically.</p>
                <input value={signature} onChange={(e)=>setSignature(e.target.value)} placeholder="Your full name" className="w-full border border-zinc-300 rounded-lg px-3 py-2" data-testid="public-signature-input"/>
              </>
            ) : (
              <>
                <p className="text-sm text-zinc-600 mb-4">Any feedback for us? (optional)</p>
                <textarea value={reason} onChange={(e)=>setReason(e.target.value)} rows={3} placeholder="Price, timing, etc." className="w-full border border-zinc-300 rounded-lg px-3 py-2" data-testid="public-decline-reason-input"/>
              </>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={()=>setDecideOpen(null)} className="px-4 py-2 border border-zinc-300 rounded-lg text-sm">Cancel</button>
              <button
                onClick={()=>decide(decideOpen)}
                disabled={submitting || (decideOpen === "accept" && !signature.trim())}
                className={`px-4 py-2 rounded-lg text-sm text-white ${decideOpen === "accept" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"} disabled:opacity-50`}
                data-testid="public-decide-confirm-btn"
              >
                {submitting ? "Sending…" : (decideOpen === "accept" ? "Sign & accept" : "Decline")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex items-center justify-between"><span className="text-zinc-600">{label}</span><span className="font-mono-accent text-zinc-900">{value}</span></div>;
}

function FullPage({ children }) {
  return <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-8">{children}</div>;
}
